'use strict';

import React, {
    useEffect,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import _ from 'lodash';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

import {__} from '~/shared/src/utilities/messageCatalog';
import {Action} from '~/shared/src/utilities/action';
import {Format} from '~/shared/src/utilities/format';
import {Query} from '~/shared/src/utilities/query';
import {
    LoadReferenceFieldItemsParameters
} from '~/shared/src/utilities/modules/form';
import {
    LoadQueryResultParameters,
    LoadQueryResultResult
} from '~/shared/src/utilities/modules/data';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {formatRenderer} from '~/frontend/src/utilities/valueRenderers';

import {
    clearElusiveData,
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';


type FieldElusiveData = {
    cacheParameters : {
        id : number;
        parameters : {
            [ parameter : string ] : any;
        };
    } | GetDataConfig,
    response : any
};

export type GetDataConfig = {};

const setElusiveData = origSetElusiveData as ( panelKey : string, data : FieldElusiveData ) => SetElusiveDataAction;


type DefaultProps = FieldWrapperDefaultProps & {
    /**
     * The number of text rows to be displayed within the field.
     * Setting to 0 means the field will be rendered as height as it needs to fit all contents.
     */
    height : number;

    /**
     * The max number of text rows to be displayed within the field.
     * 0 means unlimited
     */
    maxHeight : number;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle : React.CSSProperties;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    height         : 1,
    maxHeight      : 0,
    inputClassNames: [],
    inputStyle     : {}
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The id for the dom element.
     */
    id? : string;

    /**
     * The content.
     */
    content? : string;

    /**
     * The content.
     */
    rawContent? : string;

    /**
     * The format for the content to display
     */
    format? : Format;

    /**
     * The query to load the content with.
     */
    query? : Query;

    /**
     * The action to get the references list with.
     */
    getDataAction? : Action;

    onGetData? : ( getDataConfig : GetDataConfig ) => Promise<string | undefined>;

    /**
     * Additional parameters for the query.
     *
     * NOTE: These are set by the parent component (e.g. Form) to hold some parameters
     * belonging to the context it is used in:
     *   - formParentItemId - the item id of a parent form, NOT the form this field is contained in
     *   - treeParentItemId - the item id of the next parent dynamic treeNodes rootItem
     *   - treeRootItemId   - the item id of the trees rootItem
     *
     * IMPORTANT: These parameters will be overridden by additionalParameters if set!
     */
    additionalQueryParameters? : Required<LoadReferenceFieldItemsParameters>[ 'query' ][ 'parameters' ];

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and form contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     * Cypher or SQL based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     */
    additionalParameters? : Required<LoadReferenceFieldItemsParameters>[ 'query' ][ 'parameters' ];

    key? : string;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


const StaticContentField = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const dispatch : AppDispatch                     = useDispatch(),
              {
                  additionalParameters,
                  additionalQueryParameters,
                  content,
                  format,
                  getDataAction,
                  height,
                  id,
                  infoTextLayout,
                  inputClassNames,
                  inputStyle,
                  labelPosition,
                  labelVisibility,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onGetData,
                  pt,
                  query,
                  rawContent,
                  uid
              }                                          = props as AllProps,
              elementId                                  = id ?? `${uid}_staticContentField`,
              elusiveData : FieldElusiveData | undefined = useSelector( ( state : StoreState ) => state.elusiveData?.[ elementId ] as FieldElusiveData | undefined ),
              queryContent                               = elusiveData?.response,
              heightStyle                                = useMemo(
                  () => {
                      const retVal = getHeightStyle( inputStyle, height, maxHeight, labelVisibility, labelPosition, infoTextLayout );

                      if ( height === 0 ) {
                          retVal.whiteSpace = 'normal';
                      }

                      return retVal;
                  },
                  [
                      height,
                      infoTextLayout,
                      inputStyle.height,
                      labelPosition,
                      labelVisibility,
                      maxHeight
                  ]
              ),
              messageResolver                            = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) => messageKey === '' ? undefined : parse( __( messageKey, messagePlaceholders ) );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : parse( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '' );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              queryParameters                            = useMemo(
                  () => {
                      let retVal : LoadQueryResultParameters[ 'parameters' ] = {};

                      if ( query?.type === 'DataStructure' ) {
                          retVal = {
                              ...additionalQueryParameters,
                              ...additionalParameters
                          };
                      }

                      // filter to parameters the query knows (placeholders contained in the query string)
                      else {
                          const additionalParametersToProcess     = {
                                    ...additionalQueryParameters,
                                    ...additionalParameters
                                },
                                additionalParametersToProcessKeys = Object.keys( additionalParametersToProcess ?? {} );

                          if ( query?.parameters?.length ) {
                              for ( const parameter of query.parameters ) {
                                  const parameterTemplate = `\$\{${parameter}\}`,
                                        parameterValue    = messageTemplateStringResolver?.( parameterTemplate, true );

                                  if ( parameterValue !== parameterTemplate ) {
                                      retVal[ parameter ] = parameterValue;
                                  }
                                  else if ( additionalQueryParameters && additionalParametersToProcessKeys.includes( parameter ) ) {
                                      retVal[ parameter ] = additionalParametersToProcess[ parameter ];
                                  }
                                  else if ( parameter === 'content' && rawContent ) {
                                      retVal[ parameter ] = messageResolver( rawContent );
                                  }
                              }
                          }
                      }

                      return retVal;
                  },
                  [
                      additionalParameters,
                      additionalQueryParameters,
                      messageResolver,
                      messageTemplateStringResolver,
                      query,
                      rawContent
                  ]
              ),
              displayValue                               = useMemo(
                  () => {
                      let retVal = query ? queryContent ?? '' : content ?? '';

                      if ( retVal ) {
                          retVal = messageResolver( String( retVal )?.replaceAll( /contenteditable="[^"]*"/gi, '' ) );

                          if ( format && typeof retVal === 'string' && retVal !== '' ) {
                              retVal = formatRenderer( retVal, format );
                          }
                      }

                      return retVal;
                  },
                  [
                      content,
                      format,
                      query,
                      queryContent,
                      messageResolver
                  ]
              );

        useEffect(
            () => {
                if ( getDataAction ) {
                    const getConfigData : GetDataConfig = {},
                          doLoad                        = async () => {
                              const data = await onGetData?.( getConfigData );

                              if ( data ) {
                                  dispatch( setElusiveData( elementId, {
                                      cacheParameters: getConfigData,
                                      response       : data !== undefined && data !== null ? String( data ) : ''
                                  } ) );
                              }
                          };

                    doLoad().catch( ( error ) => {
                        console.log( error );
                    } );
                }
                else if ( query ) {
                    const params : LoadQueryResultParameters = {
                        id        : query.id,
                        parameters: queryParameters
                    };

                    if ( !elusiveData || JSON.stringify( elusiveData.cacheParameters ) !== JSON.stringify( params ) ) {
                        // abort running load
                        xhr.abort( uid, 'loadQueryResult' );

                        const doLoad = async () => {
                            const response = await xhr.post(
                                `${BASE_URL}/api/data/loadQueryResult`,
                                params,
                                undefined,
                                uid,
                                'loadQueryResult'
                            );

                            if ( response?.data ) {
                                const responseData = response.data as LoadQueryResultResult;

                                if ( responseData.success && responseData.data?.[ 0 ] ) {
                                    const row0     = responseData.data[ 0 ],
                                          row0Keys = Object.keys( row0 ),
                                          data     = row0[ row0Keys[ 0 ] ];

                                    dispatch( setElusiveData( elementId, {
                                        cacheParameters: params,
                                        response       : data !== undefined && data !== null ? String( data ) : ''
                                    } ) );
                                }
                            }
                        };

                        doLoad().catch( ( error ) => {
                            console.log( error );
                        } );
                    }
                }
                else {
                    dispatch( clearElusiveData( elementId ) );
                }
            },
            [
                query?.id ?? 0,
                JSON.stringify( queryParameters )
            ]
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            displayValue={undefined}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            <div
                id={elementId}

                className={`yn-staticcontentfield ${inputClassNames.join( ' ' )} ${!displayValue ? 'yn-empty' : ''}`}
                style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}

                aria-describedby={`${elementId}_message`}
            >
                {displayValue}
            </div>
        </FieldWrapper>;
    }
};
StaticContentField.defaultProps = defaultProps;

export default StaticContentField;
